/**
 * 브라우저의 온라인/오프라인 상태를 Recoil 전역 상태로 관리하는 훅.
 *
 * window의 online/offline 이벤트를 구독해 상태를 갱신하고, 현재 풀고 있는 챕터가 있으면
 * 오프라인 전환·복구 이벤트를 감사 로그로도 남긴다(문제 풀이 중 네트워크가 끊긴 이력 추적용).
 */
import { useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import { networkStatusState, NetworkStatus, currentChapterIdState } from "@/recoil/atom";
import { useAuditLogger } from "./useAuditLogger";
import { useRecoilValue } from "recoil";

export const useNetworkStatus = () => {
  const [status, setStatus] = useRecoilState(networkStatusState);
  const currentChapterId = useRecoilValue(currentChapterIdState);
  const { logEvent } = useAuditLogger();

  const updateStatus = useCallback((newStatus: NetworkStatus) => {
    setStatus(newStatus);
    
    // recovered 상태일 경우 3초 후 normal로 복구
    if (newStatus === "recovered") {
      setTimeout(() => {
        setStatus("normal");
      }, 3000);
    }
  }, [setStatus]);

  useEffect(() => {
    const handleOnline = () => {
      if (currentChapterId) {
        logEvent({
          chapterId: currentChapterId,
          eventType: "NETWORK_RESTORED",
          eventAt: Date.now(),
          metadata: null,
        });
      }
      // 큐에 들어갈 시간을 벌어주기 위해 미세한 지연 후 상태 변경
      setTimeout(() => updateStatus("recovered"), 10);
    };

    const handleOffline = () => {
      updateStatus("offline");
      if (currentChapterId) {
        logEvent({
          chapterId: currentChapterId,
          eventType: "NETWORK_OFFLINE",
          eventAt: Date.now(),
          metadata: null,
        });
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // 초기 상태 확인 (이미 오프라인인 경우)
    if (!navigator.onLine) {
      setStatus("offline");
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [updateStatus, setStatus, currentChapterId, logEvent]);

  return {
    status,
    updateStatus,
  };
};
